import { useCallback, useEffect, useState } from "react";
import ProductThumbnail from "../../Component/UI/ProductThumbnail";
import { mediaUrl } from "../../Component/tool/mediaUrl";
import {
  integrationErrorMessage as errorMessage,
  integrationApi,
} from "./client";
import { number } from "./shared";

type Props = {
  lives: { seq: number; title: string; prepared?: number }[];
  seq: number;
  select: (seq: number) => void;
  detail: {
    products: any[];
    status: string;
    automation?: {
      name: string;
      status: string;
      priceMode: string;
      at: string;
    };
  } | null;
  busy: boolean;
  act: (body: any) => Promise<unknown>;
  fixedRole?: "user" | "seller";
  viewerIndex?: number;
  onViewerChange?: (index: number) => void;
};
export function IntegrationPreview({
  lives,
  seq,
  select,
  detail,
  busy,
  act,
  fixedRole,
  viewerIndex,
  onViewerChange,
}: Props) {
  const [selectedRole, setRole] = useState<"user" | "seller">("user");
  const role = fixedRole || selectedRole;
  const [selectedIndex, setSelectedIndex] = useState(0),
    [snapshot, setSnapshot] = useState<any>(null),
    [error, setError] = useState("");
  const index = viewerIndex ?? selectedIndex;
  const setIndex = useCallback(
    (value: number) =>
      onViewerChange ? onViewerChange(value) : setSelectedIndex(value),
    [onViewerChange],
  );
  const [search, setSearch] = useState("");
  const [warehouse, setWarehouse] = useState<any[]>([]);
  useEffect(() => {
    let disposed = false;
    void integrationApi
      .get("/integration-tests/warehouse")
      .then(({ data }) => {
        if (!disposed)
          setWarehouse(
            (data.items || []).map((item: any) => ({
              ...item,
              current_cnt: item.init_cnt,
              product_status: "창고",
              exposure_price_mode: "visible",
            })),
          );
      })
      .catch(() => {});
    return () => {
      disposed = true;
    };
  }, []);
  const live = lives.find((item) => item.seq === seq);
  useEffect(() => {
    setIndex(0);
    setSnapshot(null);
  }, [seq, setIndex]);
  useEffect(() => {
    if (!seq || !live?.prepared) return;
    let disposed = false,
      timer: ReturnType<typeof setTimeout>;
    const poll = async () => {
      try {
        const { data } = await integrationApi.get(
          `/integration-tests/lives/${seq}/preview`,
          { params: { index } },
        );
        if (!disposed) {
          setSnapshot(data);
          setError("");
        }
      } catch (e) {
        if (!disposed) setError(errorMessage(e));
      } finally {
        if (!disposed) timer = setTimeout(poll, 3000);
      }
    };
    void poll();
    return () => {
      disposed = true;
      clearTimeout(timer);
    };
  }, [seq, index, live?.prepared]);
  const products = seq ? detail?.products || [] : warehouse;
  const unique = products.filter(
    (item, i, all) => all.findIndex((other) => other.seq === item.seq) === i,
  );
  const visible = unique.filter(
    (item) =>
      (role === "seller" || item.product_status !== "대기") &&
      item.name.toLowerCase().includes(search.toLowerCase()),
  );
  const viewer = (operation: string, body: any = {}) =>
    act({ action: "viewer", index, operation, ...body });
  return (
    <aside className="pa-test-preview" aria-label="유저·셀러 실시간 화면">
      <header>
        {!fixedRole && (
          <div className="pa-test-preview-roles" aria-label="미리보기 역할">
            <button
              type="button"
              aria-pressed={role === "user"}
              onClick={() => setRole("user")}
            >
              유저 화면
            </button>
            <button
              type="button"
              aria-pressed={role === "seller"}
              onClick={() => setRole("seller")}
            >
              셀러 화면
            </button>
          </div>
        )}
        <label>
          화면에 표시할 라이브
          <select value={seq} onChange={(e) => select(Number(e.target.value))}>
            <option value={0}>라이브 선택</option>
            {lives.map((item) => (
              <option key={item.seq} value={item.seq}>
                #{item.seq} {item.title}
              </option>
            ))}
          </select>
        </label>
      </header>
      <div className="pa-test-preview-scroll">
        <>
          {!seq && (
            <p className="pa-test-note">
              워크피플 창고 상품 미리보기입니다. 예약하면 상품·사진·옵션이
              테스트 라이브로 자동 등록됩니다.
            </p>
          )}
          <div className="pa-test-preview-title">
            <strong>{live?.title || "워크피플 상품 창고"}</strong>
            <span>
              {seq ? detail?.status || "불러오는 중" : "예약 전"} · 상품{" "}
              {unique.length}개
            </span>
          </div>
          {detail?.automation && (
            <p className="pa-test-preview-automation" role="status">
              자동 실행 · {detail.automation.name}
              <br />
              <strong>{detail.automation.status}</strong> · 가격{" "}
              {detail.automation.priceMode === "visible" ? "공개" : "비공개"}
            </p>
          )}
          {role === "user" && !!seq && (
            <label>
              테스트 회원 번호
              <input
                type="number"
                min={0}
                max={Math.max(0, (live?.prepared || 1) - 1)}
                value={index}
                onChange={(e) =>
                  setIndex(
                    Math.min(
                      Math.max(0, Number(e.target.value)),
                      Math.max(0, (live?.prepared || 1) - 1),
                    ),
                  )
                }
              />
            </label>
          )}
          {error && (
            <p className="pa-inline-error" role="alert">
              {error}
            </p>
          )}
          <input
            aria-label="화면 상품 검색"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="상품 검색"
          />
          {!products.length && (
            <p className="pa-test-note">
              창고 상품·옵션·재고를 준비하면 목록이 채워집니다.
            </p>
          )}
          {visible.map((item) => {
            const options = products.filter(
              (row) => row.seq === item.seq && row.option_seq,
            );
            const stock = options.length
              ? options.reduce(
                  (sum, row) => sum + Number(row.option_current_cnt),
                  0,
                )
              : Number(item.current_cnt);
            const soldOut =
              stock <= 0 || ["품절", "품절노출"].includes(item.product_status);
            return (
              <article className="pa-test-preview-product" key={item.seq}>
                <ProductThumbnail
                  src={
                    item.thumbnail_img
                      ? mediaUrl(item.thumbnail_img)
                      : undefined
                  }
                  alt={item.name}
                />
                <div>
                  <small>
                    #{item.product_num || item.seq} · {item.product_status}
                  </small>
                  <strong>{item.name}</strong>
                  <span>
                    {role === "seller" ||
                    item.product_status === "판매" ||
                    item.exposure_price_mode === "visible"
                      ? `${number(item.price)}원`
                      : "가격 비공개"}{" "}
                    · 재고 {number(stock)}
                  </span>
                </div>
                {role === "seller" ? (
                  <div className="pa-test-preview-controls">
                    {["대기", "판매", "판매노출", "품절노출"].map((status) => (
                      <button
                        type="button"
                        key={status}
                        disabled={busy || !seq}
                        aria-pressed={item.product_status === status}
                        onClick={() =>
                          void act({
                            action: "product",
                            id: item.seq,
                            status,
                            priceMode: item.exposure_price_mode,
                          })
                        }
                      >
                        {status}
                      </button>
                    ))}
                    <button
                      type="button"
                      disabled={busy || !seq}
                      onClick={() =>
                        void act({
                          action: "product",
                          id: item.seq,
                          status: item.product_status,
                          priceMode:
                            item.exposure_price_mode === "visible"
                              ? "seller"
                              : "visible",
                        })
                      }
                    >
                      {item.exposure_price_mode === "visible"
                        ? "가격 비공개"
                        : "가격 공개"}
                    </button>
                  </div>
                ) : (
                  <div className="pa-test-preview-options">
                    {(options.length ? options : [item]).map((option) => (
                      <div key={option.option_seq || 0}>
                        <span>
                          {option.option1 || "기본"} {option.option2 || ""} ·{" "}
                          {number(
                            option.option_current_cnt ?? item.current_cnt,
                          )}
                          개
                        </span>
                        <button
                          type="button"
                          disabled={
                            busy ||
                            !live?.prepared ||
                            !["방송중", "리허설"].includes(
                              detail?.status || "",
                            ) ||
                            !["판매", "판매노출", "품절노출"].includes(
                              item.product_status,
                            ) ||
                            snapshot?.coupons?.products?.some(
                              (row: any) =>
                                Number(row.product_seq) === item.seq &&
                                !row.can_purchase,
                            ) ||
                            (item.product_status !== "판매" &&
                              item.exposure_price_mode !== "visible") ||
                            (!soldOut &&
                              Number(
                                option.option_current_cnt ?? item.current_cnt,
                              ) <= 0)
                          }
                          onClick={() =>
                            void viewer(soldOut ? "wait" : "buy", {
                              product: item.seq,
                              option: option.option_seq,
                            })
                          }
                        >
                          {soldOut ? "재입고 대기" : "구매"}
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </article>
            );
          })}
          {role === "user" && !!seq && (
            <section className="pa-test-preview-cart">
              <h3>장바구니·구매내역</h3>
              {!snapshot?.cart?.order_detail?.length && (
                <p>담긴 상품이 없습니다.</p>
              )}
              {snapshot?.cart?.order_detail?.map((item: any) => (
                <article key={item.seq}>
                  <span>
                    {item.product_name} {item.option_name}
                    <br />
                    {number(item.order_cnt)}개 · {number(item.order_price)}원
                  </span>
                  <button
                    type="button"
                    disabled={busy || !seq}
                    onClick={() => void viewer("cancel", { detail: item.seq })}
                  >
                    1개 취소
                  </button>
                </article>
              ))}
              <h3>재입고 대기</h3>
              {!snapshot?.waits?.items?.length && (
                <p>대기 중인 상품이 없습니다.</p>
              )}
              {snapshot?.waits?.items?.map((item: any) => (
                <article key={item.group}>
                  <span>
                    {item.offer?.productName ||
                      `상품 #${item.group.split(":")[1]}`}{" "}
                    · {item.offer ? "구매 안내 도착" : `${item.rank}번째 대기`}
                  </span>
                  <button
                    type="button"
                    disabled={busy || !seq}
                    onClick={() =>
                      void viewer("leave-wait", {
                        product: Number(item.group.split(":")[1]),
                        option: Number(item.group.split(":")[2]),
                      })
                    }
                  >
                    대기 취소
                  </button>
                </article>
              ))}
              {!!snapshot?.cart?.cancellations?.length && (
                <p>취소 내역 {number(snapshot.cart.cancellations.length)}건</p>
              )}
            </section>
          )}
        </>
      </div>
    </aside>
  );
}
